import Range from 'rc-slider'
import React, { useEffect, useRef, useState } from 'react'

import { formatNumberWithThousands } from '../../../helpers/common'
import { useDebounce } from '../../../hooks/useDebounce'
import styles from './InputRangeSingle.module.scss'

interface InputRangeSingleProps {
  min: number
  max: number
  onChange: (input: HTMLInputElement) => void
  name: string
  label?: null | string
  labelStart?: boolean
  startValue?: number
  placeholder?: string | undefined
  disableInput?: boolean
  nameEventReset?: string
  step?: number
}

function InputRangeSingle({
  onChange,
  name,
  min,
  max,
  label = null,
  labelStart,
  startValue = 0,
  placeholder = '',
  disableInput = false,
  nameEventReset,
  step = 1,
}: InputRangeSingleProps) {
  const [value, setValue] = useState<number>(
    startValue > max ? max : startValue < min ? min : startValue
  )
  const [init, setInit] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  function inputHandler(e: React.FormEvent<HTMLInputElement>) {
    setValue(+e.currentTarget.value)
  }

  function blurInput(e: React.FormEvent<HTMLInputElement>) {
    const value = +e.currentTarget.value
    setValue(value > max ? max : value < min ? min : value)
  }

  function rangeHandler(value: number | number[]) {
    if (Array.isArray(value)) return

    setValue(value)
  }

  function reset() {
    setInit(false)
    setValue(startValue > max ? max : startValue < min ? min : startValue)

    setTimeout(() => {
      setInit(true)
    }, 1000)
  }

  const debounceValue = useDebounce<number>(value, 500)

  useEffect(() => {
    if (inputRef.current && init) {
      onChange(inputRef.current)
    } else {
      setInit(true)
    }
  }, [debounceValue])

  useEffect(() => {
    setValue(startValue > max ? max : startValue < min ? min : startValue)

    if (nameEventReset) {
      document.addEventListener(nameEventReset, reset)
    }

    return () => {
      if (nameEventReset) {
        document.removeEventListener(nameEventReset, reset)
      }
    }
  }, [startValue])

  useEffect(() => {
    setInit(true)
  }, [])

  return (
    <div className={styles.wrapper}>
      <label className='input-wrapper'>
        <input
          ref={inputRef}
          className={`input-value-bold ${styles.input}`}
          value={formatNumberWithThousands(value.toString())}
          onInput={inputHandler}
          onBlur={blurInput}
          placeholder={placeholder}
          name={name}
          disabled={disableInput}
          type='text'
        />
        {label && (
          <span style={{ order: `${labelStart ? -1 : 1}` }}>
            {label}
            {labelStart && <span>&nbsp;</span>}
          </span>
        )}
      </label>
      <Range
        onChange={rangeHandler}
        step={step}
        value={value}
        max={max}
        min={min}
      />
    </div>
  )
}

export default InputRangeSingle
